import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { CHAT, EVERYONE, SABOTEURS } from '@continuum/show-core';
import type { WireChat } from '@continuum/show-core';
import { PostLine, mono, sans } from '../ui/atoms.js';
import { useCover, useSheetLayer } from '../ui/OverlayHost.js';
import type { Lang } from '../ui/text.js';
import { CHAT_COPY, names, problemText } from './chatText.js';
import type { ChatSnapshot, Outgoing } from './store.js';
import { inThread, isGroup, receiptFor, threadRows } from './threads.js';
import type { Receipt, ThreadKey } from './threads.js';

const CREW = '#4fd8c2';
const AGENT = '#ff5f86';
const MUTED = '#9db3c2';

/**
 * The messages, full screen over the phone frame: the list of crewmates, and one conversation.
 *
 * It sits below every pop-up on purpose. It is not a sheet, so an alarm or a meeting still drops
 * in over it (and the app closes it when one needs this phone), and like an open game it moves
 * down for whatever banner is showing, so no words ever lie over words.
 */
export function ChatPanel({
  lang,
  seat,
  snap,
  crew,
  nameOf,
  postOf,
  thread,
  draft,
  onThread,
  onDraft,
  onSend,
  onRetry,
  onRead,
  onClose,
}: {
  lang: Lang;
  seat: number;
  snap: ChatSnapshot;
  /** Everyone checked in, by seat. */
  crew: readonly number[];
  nameOf: (seat: number) => string;
  /** A guest's post, in this phone's language, for under their call sign. */
  postOf: (seat: number) => string;
  thread: ThreadKey | null;
  draft: string;
  onThread: (key: ThreadKey | null) => void;
  onDraft: (text: string) => void;
  onSend: () => void;
  onRetry: (message: Outgoing) => void;
  onRead: (key: ThreadKey) => void;
  onClose: () => void;
}) {
  const c = CHAT_COPY[lang];
  const layer = useSheetLayer();
  useCover('chat', true);
  const keyboard = useKeyboardInset();
  const secret = thread === SABOTEURS;
  const accent = secret ? AGENT : CREW;
  const title = thread === null ? c.title : secret ? c.saboteurs : thread === EVERYONE ? c.everyone : nameOf(thread);
  const others = (snap.saboteurs ?? []).filter((s) => s !== seat).map(nameOf);

  const panel = (
    <div
      role="dialog"
      aria-label={title}
      className="cc-sheet"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 40,
        display: 'flex',
        flexDirection: 'column',
        background: '#05070b',
        color: '#dfe9f2',
        pointerEvents: 'auto',
        // Below whatever banner is showing (BannerHost sets --banner-h), never under it.
        paddingTop: 'calc(env(safe-area-inset-top) + var(--banner-h, 0px))',
        transition: 'padding-top .25s ease',
        paddingBottom: keyboard > 0 ? keyboard : 'env(safe-area-inset-bottom)',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px 10px',
          borderBottom: '1px solid rgba(255,255,255,.08)',
        }}
      >
        {thread !== null && (
          <button onClick={() => onThread(null)} aria-label={c.back} style={roundButton}>
            ‹
          </button>
        )}
        <div style={{ flex: 1, minWidth: 0, paddingLeft: thread === null ? 8 : 0 }}>
          <div style={{ ...ellipsis, fontFamily: sans, fontSize: 22, fontWeight: 700, letterSpacing: 1.5, color: thread === null ? '#dfe9f2' : accent }}>
            {title}
          </div>
          {typeof thread === 'number' && (
            <div style={{ ...ellipsis, fontFamily: mono, fontSize: 11, letterSpacing: 1, color: MUTED }}>
              {postOf(thread) ? `${postOf(thread)} · ${c.seat(thread)}` : c.seat(thread)}
            </div>
          )}
          {secret && others.length > 0 && (
            <div style={{ ...ellipsis, fontFamily: mono, fontSize: 11, letterSpacing: 0.5, color: MUTED }}>{c.saboteursWho(names(others, lang))}</div>
          )}
          {thread === EVERYONE && <div style={{ ...ellipsis, fontFamily: mono, fontSize: 11, letterSpacing: 0.5, color: MUTED }}>{c.everyoneWho}</div>}
        </div>
        <button onClick={onClose} aria-label={c.close} style={roundButton}>
          ✕
        </button>
      </div>

      {thread === null ? (
        <ThreadList lang={lang} seat={seat} snap={snap} crew={crew} nameOf={nameOf} postOf={postOf} others={others} onThread={onThread} />
      ) : (
        <Thread
          lang={lang}
          seat={seat}
          snap={snap}
          thread={thread}
          crew={crew}
          nameOf={nameOf}
          draft={draft}
          accent={accent}
          onDraft={onDraft}
          onSend={onSend}
          onRetry={onRetry}
          onRead={onRead}
        />
      )}
    </div>
  );
  return layer ? createPortal(panel, layer) : panel;
}

function ThreadList({
  lang,
  seat,
  snap,
  crew,
  nameOf,
  postOf,
  others,
  onThread,
}: {
  lang: Lang;
  seat: number;
  snap: ChatSnapshot;
  crew: readonly number[];
  nameOf: (seat: number) => string;
  postOf: (seat: number) => string;
  others: readonly string[];
  onThread: (key: ThreadKey) => void;
}) {
  const c = CHAT_COPY[lang];
  const rows = threadRows(snap.messages, seat, snap.read, crew, snap.saboteurs !== null);
  return (
    <div style={scroller}>
      <div style={{ fontFamily: mono, fontSize: 13, lineHeight: 1.5, color: MUTED, margin: '4px 2px 12px' }}>{c.lede}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((row) => {
          const secret = row.key === SABOTEURS;
          const all = row.key === EVERYONE;
          const accent = secret ? AGENT : CREW;
          const preview = row.last
            ? `${row.last.from === seat ? `${c.you}: ` : isGroup(row.key) ? `${nameOf(row.last.from)}: ` : ''}${row.last.text}`
            : secret
              ? c.saboteursWho(names(others, lang))
              : all
                ? c.everyoneWho
                : c.seat(row.key as number);
          return (
            <button
              key={String(row.key)}
              onClick={() => onThread(row.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                minHeight: 60,
                padding: '8px 12px',
                borderRadius: 12,
                border: `1px solid ${secret ? `${AGENT}88` : 'rgba(255,255,255,.14)'}`,
                background: row.unread > 0 ? 'rgba(255,255,255,.06)' : 'transparent',
                color: 'inherit',
                textAlign: 'left',
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ ...ellipsis, display: 'block', fontFamily: sans, fontSize: 18, fontWeight: 700, letterSpacing: 1.2, color: secret ? AGENT : '#dfe9f2' }}>
                  {secret ? c.saboteurs : all ? c.everyone : nameOf(row.key as number)}
                </span>
                {!isGroup(row.key) && <PostLine color="#6f808b">{postOf(row.key)}</PostLine>}
                <span style={{ ...ellipsis, display: 'block', marginTop: 2, fontFamily: mono, fontSize: 12, color: row.unread > 0 ? '#dfe9f2' : MUTED }}>
                  {preview}
                </span>
              </span>
              {row.unread > 0 && <Badge count={row.unread} color={accent} />}
              <span aria-hidden="true" style={{ flex: 'none', fontSize: 18, color: MUTED }}>
                ›
              </span>
            </button>
          );
        })}
        {crew.every((x) => x === seat) && <div style={{ fontFamily: mono, fontSize: 13, color: MUTED, lineHeight: 1.5 }}>{c.nobodyYet}</div>}
      </div>
    </div>
  );
}

function Thread({
  lang,
  seat,
  snap,
  thread,
  crew,
  nameOf,
  draft,
  accent,
  onDraft,
  onSend,
  onRetry,
  onRead,
}: {
  lang: Lang;
  seat: number;
  snap: ChatSnapshot;
  thread: ThreadKey;
  crew: readonly number[];
  nameOf: (seat: number) => string;
  draft: string;
  accent: string;
  onDraft: (text: string) => void;
  onSend: () => void;
  onRetry: (message: Outgoing) => void;
  onRead: (key: ThreadKey) => void;
}) {
  const c = CHAT_COPY[lang];
  const secret = thread === SABOTEURS;
  const group = isGroup(thread);
  const messages = inThread(snap.messages, seat, thread);
  const waiting = snap.outgoing.filter((o) => o.to === thread);
  const list = useRef<HTMLDivElement | null>(null);
  const last = messages.at(-1)?.n ?? 0;
  // Sent and Read, under this guest's own latest message only.
  const members = thread === EVERYONE ? crew : secret ? (snap.saboteurs ?? []) : [];
  const receipt = receiptFor(snap.messages, seat, thread, snap.seen, members);

  // Read as it comes in, while the guest is looking at it.
  useEffect(() => {
    if (document.visibilityState === 'visible') onRead(thread);
    const seen = () => {
      if (document.visibilityState === 'visible') onRead(thread);
    };
    document.addEventListener('visibilitychange', seen);
    return () => document.removeEventListener('visibilitychange', seen);
  }, [thread, last, onRead]);

  // The newest at the bottom, in view.
  useLayoutEffect(() => {
    const el = list.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread, last, waiting.length]);

  const left = CHAT.MAX_TEXT - draft.length;
  return (
    <>
      <div ref={list} style={{ ...scroller, gap: 6 }}>
        {messages.length === 0 && waiting.length === 0 && (
          <div style={{ margin: 'auto 8px', textAlign: 'center', fontFamily: mono, fontSize: 13, lineHeight: 1.6, color: MUTED }}>
            {secret ? c.saboteursEmpty : thread === EVERYONE ? c.everyoneEmpty : c.threadEmpty(nameOf(thread))}
          </div>
        )}
        {messages.map((m, i) => (
          <Bubble
            key={m.n}
            message={m}
            mine={m.from === seat}
            // In the table and the saboteurs' channel, who said it, once for each run of one person's messages.
            who={group && m.from !== seat && messages[i - 1]?.from !== m.from ? nameOf(m.from) : null}
            accent={accent}
            lang={lang}
            receipt={receipt && receipt.n === m.n ? receiptText(receipt.receipt, c, lang, nameOf) : null}
            read={receipt?.n === m.n && receipt.receipt.kind !== 'sent'}
          />
        ))}
        {waiting.map((o) => (
          <button
            key={o.id}
            disabled={o.error === null}
            onClick={() => onRetry(o)}
            style={{
              alignSelf: 'flex-end',
              maxWidth: '82%',
              minHeight: 0,
              padding: '8px 12px',
              borderRadius: 14,
              border: `1px solid ${o.error ? '#ff453a' : `${accent}55`}`,
              background: 'transparent',
              color: 'inherit',
              textAlign: 'left',
              opacity: o.error ? 1 : 0.6,
            }}
          >
            <span style={bubbleText}>{o.text}</span>
            <span style={{ display: 'block', marginTop: 4, fontFamily: mono, fontSize: 11, lineHeight: 1.4, color: o.error ? '#ff8a80' : MUTED }}>
              {o.error ? `${c.notSent(problemText(o.error, lang))} ${c.tapToEdit}` : c.sending}
            </span>
          </button>
        ))}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSend();
        }}
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'flex-end',
          gap: 8,
          padding: '10px 12px 12px',
          borderTop: '1px solid rgba(255,255,255,.08)',
        }}
      >
        <label style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <input
            value={draft}
            onChange={(e) => onDraft(e.target.value)}
            maxLength={CHAT.MAX_TEXT}
            placeholder={c.placeholder}
            aria-label={c.placeholder}
            enterKeyHint="send"
            autoComplete="off"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              minHeight: 44,
              padding: '10px 12px',
              borderRadius: 12,
              border: `1px solid ${accent}66`,
              background: 'rgba(255,255,255,.05)',
              color: '#eef4f8',
              // 16 px or more, or iOS zooms the page when the box is tapped.
              fontFamily: sans,
              fontSize: 17,
              fontWeight: 500,
            }}
          />
          {left <= 40 && <span style={{ fontFamily: mono, fontSize: 11, color: left <= 10 ? '#ffb547' : MUTED }}>{c.charsLeft(left)}</span>}
        </label>
        <button
          type="submit"
          disabled={!draft.trim()}
          style={{
            flex: 'none',
            minHeight: 44,
            padding: '0 16px',
            borderRadius: 12,
            border: 'none',
            background: accent,
            color: '#05070b',
            fontFamily: sans,
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: 2,
            opacity: draft.trim() ? 1 : 0.45,
          }}
        >
          {c.send}
        </button>
      </form>
    </>
  );
}

function Bubble({
  message,
  mine,
  who,
  accent,
  lang,
  receipt = null,
  read = false,
}: {
  message: WireChat;
  mine: boolean;
  who: string | null;
  accent: string;
  lang: Lang;
  /** Sent or Read, under the guest's own latest message. */
  receipt?: string | null;
  read?: boolean;
}) {
  return (
    <div style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '82%', display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
      {who && <span style={{ margin: '6px 4px 2px', fontFamily: mono, fontSize: 11, letterSpacing: 1, color: accent }}>{who}</span>}
      <div
        style={{
          padding: '8px 12px',
          borderRadius: 14,
          border: `1px solid ${mine ? `${accent}66` : 'rgba(255,255,255,.12)'}`,
          background: mine ? `${accent}22` : 'rgba(255,255,255,.06)',
        }}
      >
        {/* Text, never a link: what a guest types is shown exactly as typed. */}
        <span style={bubbleText}>{message.text}</span>
      </div>
      <span style={{ margin: '2px 4px 0', fontFamily: mono, fontSize: 10, color: MUTED, opacity: 0.8 }}>{clock(message.at, lang)}</span>
      {receipt && (
        <span style={{ margin: '1px 4px 0', fontFamily: mono, fontSize: 10, letterSpacing: 0.5, lineHeight: 1.4, textAlign: 'right', color: read ? accent : MUTED }}>
          {receipt}
        </span>
      )}
    </div>
  );
}

function receiptText(receipt: Receipt, c: (typeof CHAT_COPY)[Lang], lang: Lang, nameOf: (seat: number) => string): string {
  switch (receipt.kind) {
    case 'sent':
      return c.sent;
    case 'read':
      return c.readAt(clock(receipt.at, lang));
    case 'readBy':
      return receipt.all ? c.readByAll : c.readBy(names(receipt.seats.map(nameOf), lang).toUpperCase());
  }
}

export function Badge({ count, color = '#ff453a' }: { count: number; color?: string }) {
  return (
    <span
      style={{
        flex: 'none',
        minWidth: 20,
        height: 20,
        padding: '0 6px',
        boxSizing: 'border-box',
        borderRadius: 10,
        background: color,
        color: '#05070b',
        fontFamily: mono,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: '20px',
        textAlign: 'center',
      }}
    >
      {count > 9 ? '9+' : count}
    </span>
  );
}

function clock(at: number, lang: Lang): string {
  return new Date(at).toLocaleTimeString(lang === 'no' ? 'nb-NO' : 'en-GB', { hour: '2-digit', minute: '2-digit' });
}

/** How far the on-screen keyboard reaches up, so the box being typed in stays above it. */
function useKeyboardInset(): number {
  const [inset, setInset] = useState(0);
  useEffect(() => {
    const view = window.visualViewport;
    if (!view) return;
    const update = () => setInset(Math.max(0, Math.round(window.innerHeight - view.height - view.offsetTop)));
    update();
    view.addEventListener('resize', update);
    view.addEventListener('scroll', update);
    return () => {
      view.removeEventListener('resize', update);
      view.removeEventListener('scroll', update);
    };
  }, []);
  return inset;
}

const ellipsis: CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

const scroller: CSSProperties = {
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  overscrollBehavior: 'contain',
  WebkitOverflowScrolling: 'touch',
  display: 'flex',
  flexDirection: 'column',
  padding: '10px 14px 14px',
};

const bubbleText: CSSProperties = {
  display: 'block',
  fontFamily: sans,
  fontSize: 17,
  fontWeight: 500,
  lineHeight: 1.3,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
};

const roundButton: CSSProperties = {
  flex: 'none',
  width: 44,
  height: 44,
  borderRadius: 22,
  border: '1px solid rgba(255,255,255,.2)',
  background: 'rgba(255,255,255,.06)',
  color: '#dfe9f2',
  fontSize: 18,
  lineHeight: 1,
};
